import { Injectable } from '@angular/core';
import { BehaviorSubject, from, of, Observable } from 'rxjs';
import { doc, getDoc, DocumentReference, Firestore } from '@angular/fire/firestore';
import { map, catchError } from 'rxjs/operators';

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  itemIds: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatPaginationService {
  private paginationStates = new Map<string, BehaviorSubject<PaginationState>>();

  constructor(private firestore: Firestore) {}

  /**
   * Initialize pagination for a specific collection field
   */
  initializePagination(userId: string, collectionField: string, pageSize: number = 50): BehaviorSubject<PaginationState> {
    const key = `${userId}:${collectionField}`;

    if (!this.paginationStates.has(key)) {
      this.paginationStates.set(
        key,
        new BehaviorSubject<PaginationState>({
          currentPage: 0,
          pageSize,
          totalPages: 0,
          itemIds: []
        })
      );
    }

    const state = this.paginationStates.get(key);
    if (!state) {
      throw new Error(`No pagination state found for key: ${key}`);
    }
    return state;
  }

  /**
   * Get pagination state for a specific collection field
   */
  getPaginationState(userId: string, collectionField: string): BehaviorSubject<PaginationState> | null {
    const key = `${userId}:${collectionField}`;
    return this.paginationStates.get(key) || null;
  }

  /**
   * Clear pagination state
   */
  clearPagination(userId: string, collectionField: string): void {
    const key = `${userId}:${collectionField}`;
    this.paginationStates.delete(key);
  }

  /**
   * Generic pagination method - fetches next page from user's sub-collection
   * @param userDocPath Full path to user document (e.g., 'users/uid')
   * @param collectionField Field name containing chat array (e.g., 'chats', 'chats2', etc.)
   * @param pageSize Number of items per page
   * @returns Observable of chat items for the page
   */
  async getPageFromCollection(userDocPath: string, collectionField: string, _pageSize: number = 50): Promise<any[]> {
    try {
      const userDocRef: DocumentReference = doc(this.firestore, userDocPath);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        return [];
      }

      const data = userDocSnapshot.data();
      const itemIds = data?.[collectionField] || [];

      if (!Array.isArray(itemIds) || itemIds.length === 0) {
        return [];
      }

      // Return items (these are already IDs or references stored in the array)
      return itemIds;
    } catch (error) {
      console.error('Error fetching page from collection:', error);
      return [];
    }
  }

  /**
   * Generic pagination method - consolidates logic for all chat pagination methods
   * Fetches more chats from a specific collection field (chats, chats2, chats3, chats4, etc.)
   *
   * @param userDocPath Full path to user document
   * @param fieldName Which collection field to read from (e.g., 'chats', 'chats2')
   * @param startIndex Starting index for fetching items
   * @param pageSize Number of items to fetch
   * @returns Observable of chat items
   */
  getMoreChatsFromField(userDocPath: string, fieldName: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return from(this.getPageFromCollection(userDocPath, fieldName, pageSize)).pipe(
      map((items: any[]) =>
        // Return items from startIndex to startIndex + pageSize
        items.slice(startIndex, startIndex + pageSize)
      ),
      catchError(error => {
        console.error(`Error fetching chats from field '${fieldName}':`, error);
        return of([]);
      })
    );
  }

  /**
   * Get more chats from the 'chats' field
   */
  getMoreChats(userDocPath: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return this.getMoreChatsFromField(userDocPath, 'chats', startIndex, pageSize);
  }

  /**
   * Get more chats from the 'chats2' field
   */
  getMoreChats2(userDocPath: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return this.getMoreChatsFromField(userDocPath, 'chats2', startIndex, pageSize);
  }

  /**
   * Get more chats from the 'chats3' field
   */
  getMoreChats3(userDocPath: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return this.getMoreChatsFromField(userDocPath, 'chats3', startIndex, pageSize);
  }

  /**
   * Get more chats from the 'chats4' field
   */
  getMoreChats4(userDocPath: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return this.getMoreChatsFromField(userDocPath, 'chats4', startIndex, pageSize);
  }

  /**
   * Get more chats from the 'chats5' field
   */
  getMoreChats5(userDocPath: string, startIndex: number = 0, pageSize: number = 50): Observable<any[]> {
    return this.getMoreChatsFromField(userDocPath, 'chats5', startIndex, pageSize);
  }
}
